<template>
  <div>
    <breadcrumbs :breadcrumb-items="breadcrumbItems" />

    <UserToolbar
      v-if="account && Object.keys(account).length !== 0 && shouldShowToolbar()"
      :account="account"
    />

    <b-card v-if="adTitle">
      <GoogleAdsToolbar :title="adTitle" />
    </b-card>

    <b-card class="card-statistics">
      <div class="card-statistics-tools mb-2">
        <div class="head-card">
          <div class="mt-2">
            <StatsDateRangeFilter
              v-model="rangePicker"
              :is-loading="loading"
              class="offerwall-stats-filters"
              @range-change="toggleRange"
              @refresh="refreshData"
            >
              <template #additional-filters>
                <b-form-select
                  v-if="canViewAdUnitPathStats && availableAdUnitPaths.length"
                  v-model="selectedAdUnitPath"
                  :options="adUnitPathOptions"
                  :disabled="loading"
                  :aria-label="$t('offerwall_stats.all_ad_unit_paths')"
                  size="sm"
                  class="ad-unit-path-filter ml-1"
                  @change="selectAdUnitPath"
                />
              </template>
            </StatsDateRangeFilter>
          </div>
          <div
            v-if="statsSources.length > 1"
            class="head-card-bottom mt-2"
            role="tablist"
          >
            <b-button
              v-for="source in statsSources"
              :key="source.key"
              v-ripple.400="'rgba(113, 102, 240, 0.15)'"
              :class="{ 'router-link-active': source.key === activeSourceKey }"
              variant="flat-primary"
              :disabled="loading"
              pill
              role="tab"
              :aria-selected="source.key === activeSourceKey ? 'true' : 'false'"
              @click="selectStatsSource(source)"
            >
              {{ source.label }}
            </b-button>
          </div>
        </div>
      </div>
      <div
        v-if="loading"
        class="d-flex justify-content-center mb-1"
      >
        <b-spinner label="Loading..." />
      </div>
      <div
        v-else-if="!hasError"
        class="stats-table-scroll"
      >
        <b-table
          small
          :items="statsData"
          :fields="dynamicFields"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :sort-direction="sortDirection"
          striped
          foot-clone
          class="stats-table"
        >
          <template #table-colgroup="{ fields }">
            <col
              v-for="field in fields"
              :key="field.key"
              :style="field.key === 'date' ? { width: '200px' } : null"
            >
          </template>

          <template #head(date)>
            {{ $t('message.tableHeader.date') }}
          </template>

          <template #head(impressions)>
            {{ $t('message.tableHeader.impressions') }}
          </template>

          <template #head(clicks)>
            {{ $t('message.tableHeader.clicks') }}
          </template>

          <template #head(site_revenue)>
            Revenue
          </template>

          <template #head(phoenix_revenue)>
            Tax
          </template>

          <template #head(revenue)>
            <span v-if="isAdmin">Total</span>
            <span v-else>{{ $t('message.tableHeader.ecpm') }}</span>
          </template>

          <template #head(ecpm)>
            eCPM
          </template>

          <template #head(ecpm_site)>
            Site eCPM
          </template>

          <template #head(ecpm_total)>
            Total eCPM
          </template>

          <template #cell(date)="row">
            <a
              v-if="canViewAdUnitPathStats && !selectedAdUnitPath && row.item._adUnitRows && row.item._adUnitRows.length"
              href="#"
              :class="row.item._showDetails === true ? 'text-secondary' : ''"
              :aria-expanded="row.item._showDetails === true ? 'true' : 'false'"
              @click.prevent="row.toggleDetails"
            >
              <strong>{{ row.item.date ? getFormattedDate(new Date(row.item.date)) : '' }}</strong>
              <feather-icon
                class="ml-15"
                :icon="row.item._showDetails === true ? 'ChevronDownIcon' : 'ChevronRightIcon'"
                size="20"
              />
            </a>
            <strong v-else>{{ row.item.date ? getFormattedDate(new Date(row.item.date)) : '' }}</strong>
          </template>

          <template #row-details="{ item }">
            <div
              v-if="canViewAdUnitPathStats && !selectedAdUnitPath"
              class="ad-unit-details"
            >
              <b-table
                small
                :items="item._adUnitRows"
                :fields="dynamicFields"
                thead-class="d-none"
                class="mb-0 ad-unit-details-table"
              >
                <template #table-colgroup="{ fields }">
                  <col
                    v-for="field in fields"
                    :key="field.key"
                    :style="field.key === 'date' ? { width: '200px' } : null"
                  >
                </template>

                <template #cell(date)="data">
                  <div class="ad-unit-path-cell">
                    <span class="ad-unit-path-short">
                      {{ getAdUnitPathTail(data.item.ad_unit_path) }}
                    </span>
                    <button
                      v-b-tooltip.hover.focus.top="{
                        title: data.item.ad_unit_path,
                        customClass: 'ad-unit-path-tooltip',
                        container: 'body',
                        boundary: 'window',
                      }"
                      type="button"
                      class="ad-unit-path-tooltip-trigger"
                      :aria-label="data.item.ad_unit_path"
                    >
                      <feather-icon
                        icon="InfoIcon"
                        size="14"
                      />
                    </button>
                  </div>
                </template>

                <template #cell(impressions)="data">
                  {{ data.value ? new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(data.value) : 0 }}
                </template>

                <template #cell(clicks)="data">
                  {{ data.value ? new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(countTotal(data.value)) : 0 }}
                </template>

                <template #cell(site_revenue)="data">
                  {{ currency('USD', countTotal(data.value), 3) }}
                </template>

                <template #cell(phoenix_revenue)="data">
                  {{ currency('USD', countTotal(data.value), 3) }}
                </template>

                <template #cell(revenue)="data">
                  {{ currency('USD', countTotal(data.value), 3) }}
                </template>

                <template #cell(ecpm)="data">
                  {{ currency('USD', calcEcpm(data.item), 3) }}
                </template>

                <template #cell(ecpm_site)="data">
                  {{ currency('USD', calcEcpmSite(data.item), 3) }}
                </template>

                <template #cell(ecpm_total)="data">
                  {{ currency('USD', calcEcpmTotalRow(data.item), 3) }}
                </template>
              </b-table>
            </div>
          </template>

          <template #cell(impressions)="data">
            {{ data.value ? new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(data.value) : 0 }}
          </template>

          <template #cell(clicks)="data">
            {{ data.value ? new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(countTotal(data.value)) : 0 }}
          </template>

          <template #cell(site_revenue)="data">
            {{ currency('USD', countTotal(data.value), 3) }}
          </template>

          <template #cell(phoenix_revenue)="data">
            {{ currency('USD', countTotal(data.value), 3) }}
          </template>

          <template #cell(revenue)="data">
            {{ currency('USD', countTotal(data.value), 3) }}
          </template>

          <template #cell(ecpm)="data">
            {{ currency('USD', calcEcpm(data.item), 3) }}
          </template>

          <template #cell(ecpm_site)="data">
            {{ currency('USD', calcEcpmSite(data.item), 3) }}
          </template>

          <template #cell(ecpm_total)="data">
            {{ currency('USD', calcEcpmTotalRow(data.item), 3) }}
          </template>

          <template #foot(date)>
            <strong>{{ $t('Records') }}: {{ statsData.length }}</strong>
          </template>

          <template #foot(impressions)>
            <strong>{{ totalFormat('impressions').toLocaleString('Ru-ru') }}</strong>
          </template>

          <template #foot(clicks)>
            <strong>{{ totalFormat('clicks').toLocaleString('Ru-ru') }}</strong>
          </template>

          <template #foot(site_revenue)>
            <strong>{{ currency('USD', totalFormat('site_revenue'), 3) }}</strong>
          </template>

          <template #foot(phoenix_revenue)>
            <strong>{{ currency('USD', totalFormat('phoenix_revenue'), 3) }}</strong>
          </template>

          <template #foot(revenue)>
            <strong>{{ currency('USD', totalFormat('revenue'), 3) }}</strong>
          </template>

          <template #foot(ecpm)>
            <strong>{{ currency('USD', totalEcpm(), 3) }}</strong>
          </template>

          <template #foot(ecpm_site)>
            <strong>{{ currency('USD', totalEcpmSite(), 3) }}</strong>
          </template>

          <template #foot(ecpm_total)>
            <strong>{{ currency('USD', totalEcpmTotal(), 3) }}</strong>
          </template>
        </b-table>
      </div>
    </b-card>
  </div>
</template>

<script>
import { isOfferWallOperator, isDashboardAdmin, hasOfferWallOperatorRole } from '@/auth/roles'
import {
  BCard,
  BTable,
  BSpinner,
  BButton,
  BFormSelect,
  VBTooltip,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import Ripple from 'vue-ripple-directive'
import Breadcrumbs from '@/views/ui/breadcrumbs/Breadcrumbs.vue'

import getFormattedDate from '@/utils/date-formatter'
import useJwt from '@/auth/jwt/useJwt'
import StatsDateRangeFilter from '@/views/dashboard/partners/stats/StatsDateRangeFilter.vue'
import { getUserData } from '@/auth/utils'
import UserToolbar from '@/views/dashboard/users/UserToolbar.vue'
import GoogleAdsToolbar from './GoogleAdsToolbar.vue'

function isOfferWallOperatorUser() {
  return isOfferWallOperator(getUserData())
}

export default {
  name: 'GoogleAdsStats',
  components: {
    Breadcrumbs,
    BCard,
    BTable,
    BSpinner,
    BButton,
    BFormSelect,
    StatsDateRangeFilter,
    UserToolbar,
    GoogleAdsToolbar,
  },
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      hasError: false,
      loading: false,
      adTitle: '',
      account: {},
      breadcrumbItems: [],
      sortBy: 'date',
      sortDesc: true,
      sortDirection: 'desc',
      getFormattedDate,
      statsData: [],
      adUnitPaths: [],
      statsSources: [],
      activeSourceKey: '',
      selectedAdUnitPath: '',
      rangePicker: [
        getFormattedDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)),
        getFormattedDate(new Date()),
      ],
    }
  },
  computed: {
    isAdmin() {
      const user = getUserData()
      return isDashboardAdmin(user) || hasOfferWallOperatorRole(user)
    },
    canViewAdUnitPathStats() {
      return isDashboardAdmin(getUserData())
    },
    dynamicFields() {
      const fields = [
        {
          key: 'date',
          label: 'Date',
          thClass: 'slot-date-column',
          tdClass: 'slot-date-column',
        },
        { key: 'impressions', label: 'Impressions', tdClass: 'td-width' },
        { key: 'clicks', label: 'Clicks', tdClass: 'td-width' },
      ]
      if (this.isAdmin) {
        fields.push({ key: 'ecpm_site', label: 'Site eCPM', tdClass: 'td-width' })
        fields.push({ key: 'site_revenue', label: 'Revenue', tdClass: 'td-width' })
        fields.push({ key: 'phoenix_revenue', label: 'Tax', tdClass: 'td-width' })
        fields.push({ key: 'ecpm_total', label: 'Total eCPM', tdClass: 'td-width' })
      } else {
        fields.push({ key: 'ecpm', label: 'eCPM', tdClass: 'td-width' })
      }
      fields.push({ key: 'revenue', label: 'Total', tdClass: 'td-width' })
      return fields
    },
    activeStatsSource() {
      return this.statsSources.find(source => source.key === this.activeSourceKey)
        || this.statsSources[0]
        || null
    },
    availableAdUnitPaths() {
      return this.adUnitPaths
    },
    adUnitPathOptions() {
      return [
        {
          value: '',
          text: this.$t('offerwall_stats.all_ad_unit_paths'),
        },
        ...this.availableAdUnitPaths.map(path => ({
          value: path,
          text: path,
        })),
      ]
    },
  },
  created() {
    this.loadData()
  },
  methods: {
    // Local currency formatter with en-US locale (dot = decimal, comma = thousands)
    currency(code, value, decimals) {
      if (code && code !== 'undefined') {
        const options = { currency: code, style: 'currency' }
        if (decimals !== undefined) {
          options.minimumFractionDigits = decimals
          options.maximumFractionDigits = decimals
        }
        const formatter = new Intl.NumberFormat('en-US', options)
        return formatter.format(value)
      }
      return ''
    },
    shouldShowToolbar() {
      if (!isOfferWallOperatorUser()) return true
      const currentUser = getUserData()
      return String(this.$route.params.userId) === String(currentUser?.id)
    },
    async loadData() {
      this.loading = true
      const { userId, adId } = this.$route.params
      let userName = ''
      let adName = adId
      let userFetched = false

      try {
        // Fetch user name for breadcrumb and UserToolbar
        const userRes = await useJwt.getUser(userId)
        this.account = userRes.data.account || {}
        userName = this.account.name || this.account.email || ''
        userFetched = !!userName
      } catch (e) {
        // OfferWall operators may not have access to some users (e.g. admins)
      }

      try {
        // Fetch ad title for breadcrumb (API returns "name" field, not "title")
        const adRes = await useJwt.getGoogleWidget(adId)
        const widget = adRes.data.googlewidget || {}
        adName = widget.name || adId
        this.setStatsSources(widget)
      } catch (e) { /* fallback to ID */ }

      if (!this.statsSources.length) {
        this.setStatsSources({})
      }

      this.adTitle = adName
      this.setBreadcrumbs(userId, userName, adName, userFetched)
      this.fetchStatsData() // Fetch initial stats
    },
    setBreadcrumbs(userId, userName, adName, userFetched) {
      if (userFetched) {
        // Full breadcrumbs with user name and profile link
        this.breadcrumbItems = [
          {
            text: 'users',
            to: '/users/',
          },
          {
            text: userName,
            to: `/users/${userId}/offerwall`,
          },
          {
            text: adName,
            to: `/users/${userId}/offerwall`,
          },
          {
            text: this.$t('Statistics'),
            active: true,
          },
        ]
      } else {
        // Simplified breadcrumbs when user data is unavailable
        this.breadcrumbItems = [
          {
            text: 'OfferWall',
            to: '/offerwall',
          },
          {
            text: adName,
            active: true,
          },
          {
            text: this.$t('Statistics'),
            active: true,
          },
        ]
      }
    },
    async fetchStatsData() {
      this.loading = true
      this.hasError = false
      if (!this.canViewAdUnitPathStats) {
        this.selectedAdUnitPath = ''
        this.adUnitPaths = []
      }
      if (!this.rangePicker || this.rangePicker.length < 2) {
        this.adUnitPaths = []
        this.statsData = []
        this.loading = false
        return
      }

      const [from, to] = this.rangePicker
      const source = this.activeStatsSource
      const params = { from, to }
      if (source?.type) {
        params.type = source.type
      }
      if (this.selectedAdUnitPath) {
        params.ad_unit_path = this.selectedAdUnitPath
      }

      const { adId } = this.$route.params
      try {
        const response = await useJwt.getGoogleWidgetStats(adId, params)
        const statsData = response.data.googlewidgetstatsv2
          || response.data.googlewidgetstats
          || []
        const rows = Array.isArray(statsData) ? statsData : []
        if (this.canViewAdUnitPathStats && !this.selectedAdUnitPath) {
          this.adUnitPaths = Array.from(new Set(rows
            .map(item => this.getAdUnitPath(item))
            .filter(Boolean)))
            .sort((a, b) => a.localeCompare(b))
        }
        this.statsData = this.aggregateStatsByDate(rows)
          .sort((a, b) => new Date(b.date) - new Date(a.date))
      } catch (error) {
        this.hasError = true
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('form.error'),
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
        if (!this.selectedAdUnitPath) {
          this.adUnitPaths = []
        }
        this.statsData = []
      } finally {
        this.loading = false
      }
    },
    toggleRange(range) {
      this.rangePicker = range
      this.selectedAdUnitPath = ''
      this.adUnitPaths = []
      this.fetchStatsData()
      this.updateRouteQuery()
    },
    refreshData() {
      this.fetchStatsData()
    },
    setStatsSources(widget) {
      const sources = [{
        key: 'rewarded',
        label: this.$t('offerwall_stats.rewarded'),
        type: 'offerwall',
      }]

      const inImage = widget.inImage || widget.inImageGpt || {}
      const slots = Array.isArray(inImage.slots) ? inImage.slots : []
      const hasInImagePath = slots.some(slot => {
        const path = typeof slot === 'string' ? slot : slot?.adUnitPath
        return typeof path === 'string' && path.trim()
      })
      if (widget.enableInImage === true || hasInImagePath) {
        sources.push({
          key: 'inimage',
          label: this.$t('offerwall_stats.in_image'),
          type: 'inimage',
        })
      }

      this.statsSources = sources
      const requestedSource = this.$route.query.source
      this.activeSourceKey = sources.some(source => source.key === requestedSource)
        ? requestedSource
        : sources[0].key
    },
    selectStatsSource(source) {
      if (source.key === this.activeSourceKey) return
      this.activeSourceKey = source.key
      this.selectedAdUnitPath = ''
      this.adUnitPaths = []
      this.statsData = []
      this.updateRouteQuery()
      this.fetchStatsData()
    },
    updateRouteQuery() {
      const query = {
        from: this.rangePicker[0],
        to: this.rangePicker[1],
      }
      if (this.activeSourceKey) {
        query.source = this.activeSourceKey
      }
      this.$router.replace({
        name: 'dashboard-google-ads-stats',
        query,
      }).catch(err => { if (err.name !== 'NavigationDuplicated') throw err })
    },
    countTotal(value) {
      if (typeof value === 'object' && value !== null) {
        return Object.values(value).reduce((acc, v) => acc + v, 0)
      }
      return value || 0
    },
    getAdUnitPath(item) {
      const rawAdUnitPath = item?.ad_unit_path || item?.adUnitPath
      return typeof rawAdUnitPath === 'string' && rawAdUnitPath.trim()
        ? rawAdUnitPath.trim()
        : ''
    },
    getAdUnitPathTail(path) {
      if (typeof path !== 'string') return '—'
      const segments = path.split('/').filter(Boolean)
      return segments[segments.length - 1] || path
    },
    selectAdUnitPath() {
      this.statsData = []
      this.fetchStatsData()
    },
    aggregateStatsByDate(rows) {
      const sumFields = [
        'impressions',
        'clicks',
        'site_revenue',
        'phoenix_revenue',
        'revenue',
      ]
      const groupedRows = new Map()
      const adUnitRowsByDate = new Map()

      rows.forEach(item => {
        const date = typeof item.date === 'string'
          ? item.date.slice(0, 10)
          : item.date
        const adUnitPath = this.getAdUnitPath(item) || '—'
        if (!groupedRows.has(date)) {
          const groupedRow = {
            ...item,
            date,
            _adUnitRows: [],
          }
          sumFields.forEach(field => {
            groupedRow[field] = 0
          })
          groupedRows.set(date, groupedRow)
          adUnitRowsByDate.set(date, new Map())
        }

        const groupedRow = groupedRows.get(date)
        sumFields.forEach(field => {
          groupedRow[field] += Number(this.countTotal(item[field])) || 0
        })

        const adUnitRows = adUnitRowsByDate.get(date)
        if (!adUnitRows.has(adUnitPath)) {
          const adUnitRow = {
            ...item,
            date,
            ad_unit_path: adUnitPath,
          }
          sumFields.forEach(field => {
            adUnitRow[field] = 0
          })
          adUnitRows.set(adUnitPath, adUnitRow)
        }

        const adUnitRow = adUnitRows.get(adUnitPath)
        sumFields.forEach(field => {
          adUnitRow[field] += Number(this.countTotal(item[field])) || 0
        })
      })

      return Array.from(groupedRows.entries()).map(([date, groupedRow]) => ({
        ...groupedRow,
        _adUnitRows: Array.from(adUnitRowsByDate.get(date).values())
          .sort((a, b) => a.ad_unit_path.localeCompare(b.ad_unit_path)),
      }))
    },
    totalFormat(field) {
      let total = 0
      this.statsData.forEach(item => {
        total += this.countTotal(item[field])
      })
      return total
    },
    // User eCPM: based on revenue (total = site_revenue + phoenix_revenue)
    calcEcpm(item) {
      const impressions = this.countTotal(item.impressions)
      if (!impressions) return 0
      return (this.countTotal(item.revenue) / impressions) * 1000
    },
    // User eCPM footer total
    totalEcpm() {
      const totalImpressions = this.totalFormat('impressions')
      if (!totalImpressions) return 0
      return (this.totalFormat('revenue') / totalImpressions) * 1000
    },
    // Admin Site eCPM: based on site_revenue only (without tax)
    calcEcpmSite(item) {
      const impressions = this.countTotal(item.impressions)
      if (!impressions) return 0
      return (this.countTotal(item.site_revenue) / impressions) * 1000
    },
    // Admin Site eCPM footer total
    totalEcpmSite() {
      const totalImpressions = this.totalFormat('impressions')
      if (!totalImpressions) return 0
      return (this.totalFormat('site_revenue') / totalImpressions) * 1000
    },
    // Admin Total eCPM: based on revenue (site_revenue + phoenix_revenue)
    calcEcpmTotalRow(item) {
      const impressions = this.countTotal(item.impressions)
      if (!impressions) return 0
      return (this.countTotal(item.revenue) / impressions) * 1000
    },
    // Admin Total eCPM footer total
    totalEcpmTotal() {
      const totalImpressions = this.totalFormat('impressions')
      if (!totalImpressions) return 0
      return (this.totalFormat('revenue') / totalImpressions) * 1000
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>
<style>
[dir] .stats-table.table th.slot-date-column,
[dir] .stats-table.table td.slot-date-column,
[dir] .ad-unit-details-table.table td.slot-date-column {
  width: 200px;
  min-width: 200px;
  max-width: 200px;
  white-space: nowrap;
}

.ad-unit-path-filter {
  flex: 1 1 20rem;
  width: auto;
  min-width: 16rem;
  max-width: 26rem;
}

.offerwall-stats-filters > .d-flex {
  flex-wrap: wrap;
  gap: 0.5rem;
}

[dir] .offerwall-stats-filters .quick-date-buttons,
[dir] .offerwall-stats-filters .col-picker,
[dir] .offerwall-stats-filters .ad-unit-path-filter {
  margin: 0 !important;
}

@media (max-width: 1200px) {
  .offerwall-stats-filters .ad-unit-path-filter {
    flex-basis: 100%;
    max-width: none;
  }
}

@media (max-width: 767.98px) {
  .offerwall-stats-filters .quick-date-buttons {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    width: 100%;
  }

  .offerwall-stats-filters .quick-date-buttons .btn {
    min-height: 44px;
    margin: 0 !important;
    white-space: normal;
  }

  .offerwall-stats-filters .col-picker {
    flex: 1 1 calc(100% - 3.25rem);
    min-width: 0;
    min-height: 44px;
  }

  .offerwall-stats-filters .col-picker .form-control {
    min-width: 0;
    height: 42px;
  }

  .offerwall-stats-filters .col-picker + .btn {
    flex: 0 0 44px;
    width: 44px;
    min-height: 44px;
    margin: 0 !important;
    padding-right: 0;
    padding-left: 0;
  }

  .offerwall-stats-filters .ad-unit-path-filter {
    flex-basis: 100%;
    width: 100%;
    min-width: 0;
    max-width: none;
    min-height: 44px;
  }
}

@media (max-width: 479.98px) {
  .offerwall-stats-filters .quick-date-buttons {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.ad-unit-details {
  margin: 0.75rem 0 0;
  overflow: hidden;
  border-top: 1px solid rgb(115 103 240 / 50%);
  border-bottom: 1px solid #7367f0;
  background: #fff;
}

.ad-unit-path-cell {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 0.375rem;
}

.ad-unit-path-short {
  flex: 1 1 auto;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  color: #5e5873;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.tooltip.ad-unit-path-tooltip .tooltip-inner {
  max-width: none;
  white-space: nowrap;
}

.ad-unit-path-tooltip-trigger {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
  color: #7367f0;
  cursor: help;
}

.ad-unit-path-tooltip-trigger:focus {
  outline: 2px solid rgba(115, 103, 240, 0.35);
  outline-offset: 2px;
}

[dir] .stats-table.table,
[dir] .ad-unit-details-table.table {
  width: 100%;
  min-width: 850px;
  table-layout: fixed;
}

.stats-table-scroll {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

[dir] .table.b-table > tbody > tr.b-table-details > td {
  padding: 0 !important;
}

[dir] .ad-unit-details-table.table {
  margin-bottom: 0;
  background: #fff;
}

[dir] .ad-unit-details-table.table tbody td {
  border-top-color: #ebe9f1;
  background: #ebebeb;
  vertical-align: middle;
}

</style>
